import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import useApi from 'hooks/useApi';
import ClientMultiSelect from './ClientMultiSelect';

interface ClientOption {
  uid: string;
  name: string;
}

interface Option {
  uid: string;
  name: string;
}

export interface AudienceValue {
  clients: string[];
  allClients: boolean;
  // Teams uid, or null for a client-wide announcement.
  process: string | null;
  // User ResourceType uids within the selected client.
  roles: string[];
}

interface AudienceSelectorProps {
  clientOptions: ClientOption[];
  value: AudienceValue;
  onChange: (value: AudienceValue) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * The three-level audience funnel: client → process → roles.
 *
 * Each level unlocks the next, and the gating is not a nicety. A Teams document and a
 * ResourceType both belong to exactly one client, so "these roles, across those two
 * clients" is a question the data cannot answer — the process picker therefore stays
 * locked until exactly one client is selected, and the roles picker until a process is.
 * Making the impossible states unreachable beats validating them after the fact.
 *
 * Where `ClientMultiSelect` disables rather than clears (flipping "All clients" back off
 * restores the previous selection), this component genuinely *clears* the levels below a
 * change. It has to: a process uid kept across a client change would point at another
 * tenant's document. The server rejects that combination too — see resolveAudience — but
 * the form must never offer it in the first place.
 *
 * Options are fetched per client and cached for the dialog's lifetime, so reopening the
 * same client's process list costs nothing.
 */
const AudienceSelector = ({ clientOptions, value, onChange, disabled = false, error = false, helperText }: AudienceSelectorProps) => {
  const { getTeams, getResourceTypes, getAnnouncementAudienceCount } = useApi();

  const [processOptions, setProcessOptions] = useState<Option[]>([]);
  const [roleOptions, setRoleOptions] = useState<Option[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [reach, setReach] = useState<number | null>(null);

  // Targeting is only offered for a single, explicitly chosen client.
  const singleClient = !value.allClients && value.clients.length === 1 ? value.clients[0] : null;
  const canPickProcess = Boolean(singleClient);
  const canPickRoles = canPickProcess && Boolean(value.process);

  // One place that enforces "clearing a level clears everything under it", so no caller
  // has to remember the rule.
  const emit = (next: Partial<AudienceValue>) => {
    const merged: AudienceValue = { ...value, ...next };
    const stillSingle = !merged.allClients && merged.clients.length === 1;
    if (!stillSingle) {
      merged.process = null;
      merged.roles = [];
    } else if (!merged.process) {
      merged.roles = [];
    }
    onChange(merged);
  };

  // Load the selected client's processes and roles together — they come from different
  // endpoints but are useless apart, and fetching both up front means selecting a process
  // does not then stall on a second request before the roles appear.
  useEffect(() => {
    if (!singleClient) {
      setProcessOptions([]);
      setRoleOptions([]);
      setOptionsError(null);
      return;
    }

    let active = true;
    setLoadingOptions(true);
    setOptionsError(null);

    Promise.all([
      getTeams({ page: 1, size: 200, clients: [singleClient] }),
      // 'user' matters: ResourceType also models vehicles and other non-people resources,
      // which must never be offered as an audience.
      getResourceTypes(singleClient, 'user')
    ])
      .then(([teamsResponse, rolesResponse]: any[]) => {
        if (!active) return;
        setProcessOptions(
          (teamsResponse?.data?.teams ?? []).filter((team: any) => team?.uid).map((team: any) => ({ uid: team.uid, name: team.name }))
        );
        // This endpoint returns the uid under `id`, not an ObjectId — the uid is what the
        // announcement payload carries.
        setRoleOptions((rolesResponse?.data ?? []).filter((role: any) => role?.id).map((role: any) => ({ uid: role.id, name: role.name })));
        setLoadingOptions(false);
      })
      .catch(() => {
        if (!active) return;
        // Surfaced inline rather than as a toast: the pickers below are the thing that is
        // broken, and an empty dropdown with no explanation reads as "this client has no
        // processes", which is a different and much more misleading message.
        setOptionsError('Could not load processes and roles for this client.');
        setProcessOptions([]);
        setRoleOptions([]);
        setLoadingOptions(false);
      });

    return () => {
      active = false;
    };
  }, [singleClient, getTeams, getResourceTypes]);

  // "This will reach N people". The funnel makes it easy to build an audience that is
  // perfectly valid and completely empty — a role nobody in that process holds — which
  // would otherwise publish successfully and reach no one.
  useEffect(() => {
    if (!singleClient || !value.process || value.roles.length === 0) {
      setReach(null);
      return;
    }

    let active = true;
    getAnnouncementAudienceCount({ client: singleClient, process: value.process, roles: value.roles })
      .then((response: any) => {
        if (!active) return;
        setReach(typeof response?.data?.count === 'number' ? response.data.count : null);
      })
      .catch(() => {
        // A missing count is not worth an error state — the save is still valid, the
        // author just does not get the reassurance.
        if (active) setReach(null);
      });

    return () => {
      active = false;
    };
  }, [singleClient, value.process, value.roles, getAnnouncementAudienceCount]);

  const selectedProcess = processOptions.find((option) => option.uid === value.process) ?? null;
  const selectedRoles = roleOptions.filter((option) => value.roles.includes(option.uid));

  const processHelper = () => {
    if (value.allClients) return 'Not available for a broadcast to all clients.';
    if (value.clients.length === 0) return 'Select one client to target a process.';
    if (value.clients.length > 1) return 'Available only when exactly one client is selected.';
    if (loadingOptions) return 'Loading processes…';
    if (!processOptions.length) return 'This client has no processes.';
    return 'Optional. Leave empty to reach everyone at this client.';
  };

  return (
    <Stack spacing={2}>
      <ClientMultiSelect
        clientOptions={clientOptions}
        value={value.clients}
        onChange={(uids) => emit({ clients: uids })}
        allClients={value.allClients}
        onAllClientsChange={(allClients) => emit({ allClients })}
        disabled={disabled}
        error={error}
        helperText={helperText}
      />

      {optionsError && <Alert severity="warning">{optionsError}</Alert>}

      <Autocomplete
        options={processOptions}
        value={selectedProcess}
        disabled={disabled || !canPickProcess || loadingOptions}
        getOptionLabel={(option) => option.name ?? ''}
        isOptionEqualToValue={(option, selected) => option.uid === selected.uid}
        onChange={(_event, newValue) => emit({ process: newValue?.uid ?? null })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Process"
            placeholder={canPickProcess ? 'All processes' : ''}
            helperText={processHelper()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingOptions && canPickProcess ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        options={roleOptions}
        value={selectedRoles}
        disabled={disabled || !canPickRoles}
        getOptionLabel={(option) => option.name ?? ''}
        isOptionEqualToValue={(option, selected) => option.uid === selected.uid}
        onChange={(_event, newValue) => emit({ roles: newValue.map((option) => option.uid) })}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => <Chip {...getTagProps({ index })} key={option.uid} label={option.name} size="small" />)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Roles"
            placeholder={canPickRoles && selectedRoles.length === 0 ? 'Select roles' : ''}
            helperText={canPickRoles ? 'Required once a process is chosen — only these roles will see it.' : 'Select a process first.'}
          />
        )}
      />

      {/* Shown only for a fully specified process/role audience, which is the only shape
          with a countable membership. Zero is called out rather than stated flatly: it is
          the one number that means the announcement will reach nobody at all. */}
      {reach !== null && (
        <Box>
          <Typography variant="caption" color={reach === 0 ? 'error.main' : 'text.secondary'}>
            {reach === 0
              ? 'No one currently holds these roles in this process — this would reach nobody.'
              : `This will reach ${reach} ${reach === 1 ? 'person' : 'people'}.`}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default AudienceSelector;
